<template>
  <div
    ref="$el"
    class="virtual-background-card"
    @click.stop="push(to)"
  >
    <div
      v-if="isDaylight"
      class="daylight"
    >
      <img
        alt="Daylight"
        height="14"
        loading="lazy"
        src="@/assets/svg/daylight.svg"
        width="14"
      >
    </div>
    <UiComponentImage
      v-if="imageWidth !== 0"
      :key="imageWidth"
      class="background"
      :alt="image.alt"
      :expected-size="imageWidth"
      :extra-params="{
        h: imageHeight,
        w: imageWidth,
        fit: 'crop',
      }"
      :height="imageHeight"
      :source="image.url"
      :width="imageWidth"
    />
    <div class="content">
      <UiComponentTag
        size="s"
        type="secondary"
      >
        <slot name="tag" />
      </UiComponentTag>
      <NuxtLink
        :prefetch="false"
        :to="to"
      >
        <UiComponentHeading
          :as="as"
          :level="2"
        >
          <slot name="paragraph" />
        </UiComponentHeading>
      </NuxtLink>
    </div>
  </div>
</template>

<script setup>
// Composables
const { push } = useRouter()

// Props
const props = defineProps({
  as: {
    default() {
      return 'heading'
    },
    required: false,
    type: String,
    validator(value) {
      return ['heading', 'paragraph'].indexOf(value) !== -1
    },
  },
  image: {
    type: Object,
    required: true,
  },
  isDaylight: {
    required: true,
    type: Boolean,
  },
  to: {
    required: true,
    type: String,
  },
})

// Refs
const $el = ref(null)
const count = ref(0)
const imageWidth = ref(254)
const ratio = ref(129 / 73)
const resizeObserver = ref(null)

// Computed Properties
const imageHeight = computed(() => {
  return Math.ceil(imageWidth.value / ratio.value)
})

// Methods
function onObserve() {
  imageWidth.value = 0
  nextTick(() => {
    const { offsetWidth } = $el.value
    imageWidth.value = offsetWidth - 36
  })
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  resizeObserver.value.unobserve($el.value)
})

onMounted(() => {
  resizeObserver.value = new ResizeObserver(onObserve)
  resizeObserver.value.observe($el.value)
})
</script>

<style lang="postcss" scoped>
.virtual-background-card {
  @apply relative flex flex-col p-4 border-2 border-grey-blue-200 rounded-4xl cursor-pointer duration-300 ease-in-out;
  transition-property: border-color, box-shadow;

  &:hover {
    @apply border-transparent shadow-light4;
  }

  .daylight {
    @apply absolute right-4 top-4 flex items-center justify-center h-8 w-8 bg-white/60 backdrop-blur-[2px] rounded-bl-[18px] rounded-tr-[18px] z-10;
  }

  .background {
    border-radius: 18px;
  }

  .content {
    @apply mt-4 px-2 flex flex-col;

    .tag {
      @apply self-start font-medium;
    }

    .h2 {
      @apply mt-2 font-medium text-origin leading-7 !important;
    }
  }
}
</style>
